import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { BreadCrumb } from '../../models/breadcrumb';
import { SessionService } from '@veritas-shared/services/session.service';
import { CommonHelper } from '@veritas-shared/helpers/common.helper';
import { combineLatest, Subscription } from 'rxjs';
import { ConstantHelper } from '@veritas-shared/helpers/constant.helper';
import { BreadcrumbService } from '@veritas-core/services/breadcrumb.service';

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss']
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  public subscription: Subscription;
  public breadcrumbs: BreadCrumb[];

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly sessionService: SessionService,
    private readonly commonHelper: CommonHelper,
    private readonly constantHelper: ConstantHelper,
    private readonly breadcrumbService: BreadcrumbService
  ) { }

  ngOnInit() {
    // Each time router changes from route, we capture NavigationEnd event.
    // buildBreadCrumbV2() method starts with the root of the current route (activatedRoute), which is the same as the route we are actually on.
    // We recursively builds the elements of the breadcrumb by enumerating children routes (declared in module) that matches the current route
    this.subscription = combineLatest([
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        distinctUntilChanged(),
        map(() => this.buildBreadCrumbV2(this.activatedRoute.root)),
      ),
      this.sessionService.changePageTitleEvent
    ]).subscribe(value => {      
      const breadcrumbs = this.breadcrumbService.getClearedUp(value[0], value[1]);
      // To avoid useless rebuild check if some things is change
      if (this.commonHelper.compareObjects(this.breadcrumbs, breadcrumbs)) return;
      this.breadcrumbs = this.commonHelper.clone(breadcrumbs);
    });
  }

  ngOnDestroy() {
    if (this.subscription) this.subscription.unsubscribe();
  }

  private buildBreadCrumbV2 = (route: ActivatedRoute, url: string = '', breadcrumbs: BreadCrumb[] = []): BreadCrumb[] => {
    // @@@DEBUG Print all configured routes -- console.log('configured routes: ', this.router.config);
    this.sessionService.setPageTitle(null);

    // If routeConfig is available and there's data associated, then try get resourceKey from data, else fallback to menu.Home
    let resourceKey = route.routeConfig 
      ? route.routeConfig.data ? route.routeConfig.data['resourceKey'] : ''
      : this.constantHelper.LBL_MENU_HOME;

    // If the current route is in edit mode, for the last child set the ResourceEditKey label
    const editQueryParams = route.snapshot.queryParams.edit;
    if (editQueryParams && route.firstChild == undefined) {
      resourceKey = route.routeConfig && route.routeConfig.data
        ? route.routeConfig.data['resourceEditKey']
        : '';
    }

    // If routeConfig is available than get the path, else fallback on root path
    const path = route.routeConfig ? route.routeConfig.path : '';

    // In the routeConfig the complete path is not available, so we rebuild it each time
    const nextUrl = `${url}${path}/`;
    const newBreadcrumbs: BreadCrumb[] = this.breadcrumbService.getUpgraded({ breadcrumbs, path, route, url, resourceKey });

    // If we are not on our current path yet,
    // there will be more children to look after, to build our breadcumb
    if (route.firstChild && route.firstChild.routeConfig.path?.trim()?.length) {
      // Recursive buildBreadCrumb call
      return this.buildBreadCrumbV2(route.firstChild, nextUrl, newBreadcrumbs);
    }

    return newBreadcrumbs;
  }
}
